import { Loader2 } from 'lucide-react'
import { cn } from '@/components/ui/cn'

interface LessonGeneratingPanelProps {
  mode: 'STORY' | 'EXERCISES'
  elapsedSeconds: number
  status?: string | null
  exerciseCount?: number
  wordCount: number
}

const LESSON_STEPS = [
  'Starting',
  'Writing scenario',
  'Building exercises',
  'Final check',
]

const STORY_STEPS = ['Starting', 'Writing story', 'Polishing text']

function lessonActiveStep(elapsedSeconds: number, status?: string | null): number {
  if (status === 'PENDING' && elapsedSeconds < 2) return 0
  if (elapsedSeconds < 10) return 1
  if (elapsedSeconds < 28) return 2
  return 3
}

function storyActiveStep(elapsedSeconds: number, status?: string | null): number {
  if (status === 'PENDING' && elapsedSeconds < 2) return 0
  if (elapsedSeconds < 12) return 1
  return 2
}

export function LessonGeneratingPanel({
  mode,
  elapsedSeconds,
  status,
  exerciseCount,
  wordCount,
}: LessonGeneratingPanelProps) {
  const steps = mode === 'STORY' ? STORY_STEPS : LESSON_STEPS
  const activeStep =
    mode === 'STORY'
      ? storyActiveStep(elapsedSeconds, status)
      : lessonActiveStep(elapsedSeconds, status)
  const progressPercent = Math.min(
    95,
    mode === 'STORY' ? 10 + elapsedSeconds * 6 : 8 + elapsedSeconds * 3,
  )

  const statusLabel =
    mode === 'EXERCISES'
      ? activeStep <= 1
        ? 'Creating a short scenario with your words…'
        : activeStep === 2
          ? 'Turning the scenario into exercises…'
          : 'Almost ready…'
      : 'AI is writing your story…'

  return (
    <div
      className="rounded-xl border bg-card p-6"
      data-testid="lesson-generating-panel"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-foreground">
            {mode === 'STORY' ? 'Generating your story' : 'Building your lesson'}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === 'STORY'
              ? 'Usually takes 10–15 seconds'
              : `Two steps: scenario, then exercises · ${exerciseCount ?? 5} exercises · ${wordCount} words`}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {elapsedSeconds > 0 ? `${elapsedSeconds}s elapsed` : 'Starting…'}
            {status === 'PROCESSING' ? ' · working' : ''}
          </p>
          <p className="mt-2 text-xs font-medium text-foreground">{statusLabel}</p>
        </div>
      </div>

      <div className="mt-5 h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all duration-700 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <ol className="mt-4 grid gap-2 sm:grid-cols-2">
        {steps.map((step, index) => (
          <li
            key={step}
            className={cn(
              'flex items-center gap-2 text-xs',
              index <= activeStep ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            <span
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold',
                index < activeStep
                  ? 'border-primary bg-primary text-primary-foreground'
                  : index === activeStep
                    ? 'border-primary text-primary'
                    : 'border-border text-muted-foreground',
              )}
            >
              {index < activeStep ? '✓' : index + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
    </div>
  )
}
